/**
 * useInactivityLogout Hook
 *
 * Automatically logs out the user after a period of inactivity.
 * Monitors mouse, keyboard, touch, and scroll events.
 */

import { useEffect, useRef, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { secureLogout } from '../utils/secureLogout';

const DEFAULT_TIMEOUT = 30 * 60 * 1000; // 30 minutes
const ACTIVITY_EVENTS = ['mousedown', 'mousemove', 'keydown', 'touchstart', 'scroll', 'click'];
const THROTTLE_MS = 60 * 1000; // Only update timer every 60s to save perf

/** Returns timeout in ms, or null if auto-logout is disabled ('never'). */
function getInactivityTimeout(): number | null {
  const stored = localStorage.getItem('inactivity_timeout');
  if (stored === 'never') return null;
  const ms = parseInt(stored || '', 10);
  return isNaN(ms) ? DEFAULT_TIMEOUT : ms;
}

export function useInactivityLogout() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastActivityRef = useRef<number>(Date.now());

  const handleLogout = useCallback(() => {
    secureLogout(dispatch as any, navigate);
  }, [dispatch, navigate]);

  const resetTimer = useCallback(() => {
    const timeout = getInactivityTimeout();
    if (timeout === null) return; // auto-logout disabled

    const now = Date.now();
    // Throttle: only reset if enough time has passed since last reset
    if (now - lastActivityRef.current < THROTTLE_MS) return;
    lastActivityRef.current = now;

    if (timerRef.current) {
      clearTimeout(timerRef.current);
    }

    timerRef.current = setTimeout(() => {
      handleLogout();
    }, timeout);
  }, [handleLogout]);

  useEffect(() => {
    // Start the initial timer
    const timeout = getInactivityTimeout();
    if (timeout !== null) {
      timerRef.current = setTimeout(() => {
        handleLogout();
      }, timeout);
    }

    // Listen for activity
    ACTIVITY_EVENTS.forEach(event => {
      window.addEventListener(event, resetTimer, { passive: true });
    });

    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
      ACTIVITY_EVENTS.forEach(event => {
        window.removeEventListener(event, resetTimer);
      });
    };
  }, [resetTimer, handleLogout]);
}

export default useInactivityLogout;
